import type { ChartSeriesToken } from '@deepdive-ui';
import { seriesToken } from './aggregate';
import { USER_BRAND } from './brand';
import { dimensionIndex, labelOf } from './dimensions';
import type { MentionRow } from './generator';
import { metrics } from './metrics';

/**
 * Where each brand stands, in the chips' own order.
 *
 * Lifted out of `CompetitorsView` on 2026-09-18, unchanged, because Overview
 * draws two of that module's cards and a second copy of this would be a second
 * place for "how big is Robi" to be answered. One grouped pass feeds every card
 * that reads it, which is what stops two of them disagreeing — the same
 * property `aggregate` guarantees for a time series, applied to the brand axis.
 *
 * Each figure is reduced over the **whole** group rather than accumulated row by
 * row, because `positiveShare` and `negativeShare` are ratios and a running fold
 * would turn them into means of means.
 *
 * Ordered by fixture position, not by size, so a brand keeps its colour and its
 * place in the legend whatever the filter does to its volume.
 */
export type Standing = {
  /** The chip's slug — `grameenphone`. */
  value: string;
  /** The chip's label — `Grameenphone`. */
  label: string;
  color: ChartSeriesToken;
  mentions: number;
  /** Share of voice: this brand's mentions over the **selected** brands' total. */
  share: number;
  positive: number;
  negative: number;
  own: boolean;
};

/**
 * Minimum mentions before a brand can be named "best" or "leader". Not in Figma.
 *
 * The same guard, and the same reason, as Brand Health's `TOPIC_RATE_FLOOR`: a
 * share computed over two rows is not a reading. Filter hard enough — one
 * channel, one intent, one sentiment — and a brand can be left holding a single
 * negative mention, which is a 100% negative rate and would crown it the worst
 * operator in the country. Three is the floor where the figure is at least
 * directional and low enough that a tightly filtered period still has a winner.
 *
 * It guards the **superlative KPIs only**, never the charts. Dropping a brand
 * from a plot because it is small would make the donut and the ranked bar
 * disagree about who is in the category, which is worse than a thin bar.
 */
export const BRAND_FLOOR = 3;

export function standingsFor(rows: readonly MentionRow[]): Standing[] {
  const byBrand = new Map<string, MentionRow[]>();
  for (const row of rows) {
    const group = byBrand.get(row.brand);
    if (group) group.push(row);
    else byBrand.set(row.brand, [row]);
  }

  const total = rows.length;

  return [...byBrand.entries()]
    .sort(([a], [b]) => (dimensionIndex.brand[a] ?? 0) - (dimensionIndex.brand[b] ?? 0))
    .map(([value, group]) => ({
      value,
      label: labelOf('brand', value),
      color: seriesToken('brand', value),
      mentions: group.length,
      share: total === 0 ? 0 : (group.length / total) * 100,
      positive: metrics.positiveShare.reduce(group),
      negative: metrics.negativeShare.reduce(group),
      own: value === USER_BRAND,
    }));
}
